import { Stack, Group, Divider, Flex } from '@mantine/core';
import { Fragment } from 'react';
import useTranslation from 'next-translate/useTranslation';

import Dialog from '~/domains/design-system/components/Dialog/Dialog';
import { Button } from '~/domains/design-system/Button';
import { Text } from '~/domains/design-system/Text';
import { Title } from '~/domains/design-system/Title';
import type { GlossaryResponse } from '~/pages/api/gamecenter/glossary/[league]/[prop]';
import type { LeagueAlias } from '~/domains/common/enums/league-alias.enum';

type GlossaryModalProps = {
  isOpen: boolean;
  isClosing?: boolean;
  onClose: () => void;
  isCloseDisabled?: boolean;
  league: LeagueAlias;
  glossaryData?: GlossaryResponse;
};

function GlossaryModal({
  isOpen,
  isClosing,
  onClose,
  isCloseDisabled,
  league,
  glossaryData,
}: GlossaryModalProps) {
  const { t } = useTranslation('contest');
  return (
    <Dialog
      type={{
        desktop: 'modal',
        mobile: 'drawer',
      }}
      isOpen={isOpen}
      isClosing={isClosing}
      onClose={isCloseDisabled ? undefined : onClose}
    >
      <Stack spacing="lg" w={{ base: '100%', md: 400 }} p="md">
        <Flex justify="center" align="center">
          <Title variant="headline-xl">{t('common.modals.glossaryModal.title')}</Title>
        </Flex>

        {glossaryData?.glossary?.length > 0 ? (
          <Stack spacing={0}>
            {glossaryData.glossary.map((item, idx) => (
              <Fragment key={item.label}>
                {idx !== 0 && <Divider color="gray.3" />}
                <Group noWrap align="start" py="sm">
                  <Text weight={600} style={{ minWidth: 100 }}>
                    {item.label}
                  </Text>
                  <Text style={{ flex: 1 }}>{item.description}</Text>
                </Group>
              </Fragment>
            ))}
            <Divider color="gray.3" />
          </Stack>
        ) : (
          <Text align="center">{t('common.modals.glossaryModal.noDataFound')}</Text>
        )}

        <Button variant="tertiary-fill" size="medium" fullWidth onClick={onClose}>
          {t('common.modals.glossaryModal.dismiss')}
        </Button>
      </Stack>
    </Dialog>
  );
}

export default GlossaryModal;
